import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  return (
    <section className="relative h-[70vh] sm:h-[80vh] md:h-[90vh] lg:h-[100vh] overflow-hidden">
      {/* Responsive Image Background */}
      <div className="absolute inset-0 w-full h-full sm:top-20 sm:h-[calc(100%-5rem)] md:top-24 md:h-[calc(100%-6rem)] lg:top-20 lg:h-[calc(100%-5rem)] xl:top-24 xl:h-[calc(100%-6rem)]">
        {/* Mobile Hero Image */}
        <motion.img
          src="/assets/hero-mobile.png"
          alt="Professional business consultation - Mobile"
          className="object-cover object-center w-full h-full block sm:hidden"
          animate={{
            scale: [1.05, 1.08, 1.05],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        
        {/* Desktop Hero Image */}
        <motion.img
          src="/assets/hero-desktop.png"
          alt="Professional business consultation - Desktop"
          className="object-cover object-center w-full h-full hidden sm:block"
          animate={{
            scale: [1.05, 1.08, 1.05],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />

        {/* Image Overlay - More opaque for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/75"></div>
      </div>

      {/* Animated gradient elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none sm:top-20 md:top-24 lg:top-20 xl:top-24">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 0.7, 0.4] }}
          transition={{ repeat: Infinity, duration: 8, ease: "easeInOut" }}
          className="absolute -right-20 top-1/4 w-96 h-96 bg-secondary/30 rounded-full blur-3xl"
        ></motion.div>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.3, 0.6, 0.3] }}
          transition={{ repeat: Infinity, duration: 10, ease: "easeInOut", delay: 2 }}
          className="absolute -left-20 bottom-1/4 w-96 h-96 bg-accent/30 rounded-full blur-3xl"
        ></motion.div>
      </div>

      {/* Hero Content */}
      <div className="absolute inset-0 flex items-center justify-center z-30 pt-24 sm:pt-20 md:pt-24 lg:pt-20 xl:pt-24">
        <div className="container-custom text-center px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-black text-white mb-4 sm:mb-6 md:mb-8 leading-tight font-heading tracking-tight">
                <span className="block mb-1">Elevate Your</span>
                <span className="relative inline-block md:block">
                  <span className="text-secondary font-black">Financial Future</span>
                  <span className="absolute -bottom-2 left-0 w-full h-1.5 bg-gradient-to-r from-secondary to-yellow-400 rounded-full"></span>
                </span>
              </h1>
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-white/95 font-semibold mb-6 md:mb-8 lg:mb-10 max-w-3xl mx-auto font-body leading-relaxed"
            >
              Unlock unprecedented growth with our <span className="text-secondary font-bold">expert financial strategies.</span> Transform your business vision into measurable success through intelligent capital management.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 mt-4 md:mt-6"
            >
              <Link to="/services" className="btn btn-secondary text-sm sm:text-base md:text-lg py-2.5 md:py-3 px-4 sm:px-6 md:px-8 shadow-xl hover:shadow-secondary/20 btn-3d">
                Explore Services
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Animated Wave Bottom Edge */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden z-20">
        <motion.svg
          className="relative block w-full h-16 sm:h-20 md:h-24 lg:h-32"
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          animate={{
            x: [0, -50, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" style={{ stopColor: 'rgba(255, 255, 255, 0.1)', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: 'rgba(255, 255, 255, 0.8)', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
          <path
            d="M0,60 C300,120 600,0 900,60 C1050,90 1150,30 1200,60 L1200,120 L0,120 Z"
            fill="url(#waveGradient)"
          />
        </motion.svg>
        
        {/* Second wave for layered effect */}
        <motion.svg
          className="absolute bottom-0 left-0 w-full h-12 sm:h-16 md:h-20 lg:h-24"
          data-name="Layer 2"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          animate={{
            x: [0, 30, 0],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          <path
            d="M0,80 C400,20 800,120 1200,80 L1200,120 L0,120 Z"
            fill="rgba(255, 255, 255, 0.6)"
          />
        </motion.svg>

        {/* Third subtle wave */}
        <motion.svg
          className="absolute bottom-0 left-0 w-full h-8 sm:h-12 md:h-16 lg:h-20"
          data-name="Layer 3"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          animate={{
            x: [0, -20, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        >
          <path
            d="M0,100 C200,60 400,140 600,100 C800,60 1000,140 1200,100 L1200,120 L0,120 Z"
            fill="rgba(255, 255, 255, 0.9)"
          />
        </motion.svg>
      </div>
    </section>
  );
};

export default HeroSection; 